import { defineStore } from 'pinia';
import { ref } from 'vue';

import {
  createVendorOrganization,
  deleteVendorOrganization,
  fetchAuth0Organizations,
  fetchVendorOrganizations,
} from '@/services/vendorOrganizationsService';
import type { Auth0Organization, VendorOrganizationInput, VendorOrganizationMapping } from '@/types/vendorOrganization';
import { extractErrorMessage } from '@/utils/apiError';

const byAuth0OrgId = (a: VendorOrganizationMapping, b: VendorOrganizationMapping): number =>
  a.auth0OrgId.localeCompare(b.auth0OrgId);

export const useVendorOrganizationsStore = defineStore('vendorOrganizations', () => {
  const items = ref<VendorOrganizationMapping[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const loaded = ref(false);
  const organizations = ref<Auth0Organization[]>([]);
  const organizationsError = ref<string | null>(null);

  async function load(): Promise<void> {
    loading.value = true;
    error.value = null;
    organizationsError.value = null;
    const [mappingsResult, organizationsResult] = await Promise.allSettled([
      fetchVendorOrganizations(),
      fetchAuth0Organizations(),
    ]);

    if (mappingsResult.status === 'fulfilled') {
      items.value = mappingsResult.value;
      loaded.value = true;
    } else {
      error.value = extractErrorMessage(mappingsResult.reason);
    }

    if (organizationsResult.status === 'fulfilled') {
      organizations.value = organizationsResult.value;
    } else {
      organizationsError.value = extractErrorMessage(organizationsResult.reason);
    }

    loading.value = false;
  }

  async function create(input: VendorOrganizationInput): Promise<void> {
    const created = await createVendorOrganization(input);
    items.value = [...items.value, created].sort(byAuth0OrgId);
  }

  async function remove(id: string): Promise<void> {
    await deleteVendorOrganization(id);
    items.value = items.value.filter((mapping) => mapping.id !== id);
  }

  function organizationLabel(orgId: string): string {
    const organization = organizations.value.find((candidate) => candidate.id === orgId);
    return organization ? organization.displayName : orgId;
  }

  return {
    items,
    loading,
    error,
    loaded,
    organizations,
    organizationsError,
    load,
    create,
    remove,
    organizationLabel,
  };
});
